import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert, Stack, Chip,
} from '@mui/material';
import {
  Print, PictureAsPdf, GridOn, Refresh, Book, CheckCircle, ErrorOutline,
} from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { reportCashBookURL } from '../../BaseURL';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateLong = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'long', year: 'numeric' });
};

const fmtDateShort = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'short' });
};

const NAVY  = '#0B2545';
const GREEN = '#0F766E';
const RED   = '#B91C1C';
const GOLD  = '#C89211';
const RULE  = '#0B2545';

export default function CashBookReport({ start, end }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const printRef = useRef(null);

  const load = () => {
    if (!start || !end) return;
    setLoading(true); setError('');
    axios.get(reportCashBookURL, {
      headers: headers(),
      params: { start, end },
    })
      .then(r => { if (r.data.success) setData(r.data); else setError(r.data.message); })
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  // ─── PRINT ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank', 'width=1200,height=820');
    if (!w) return;
    w.document.write(`
      <html><head><title>Cash Book - ${data?.period.label || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 20mm 12mm; color: #111; }
        .letterhead { display: flex; align-items: center; gap: 16px; padding-bottom: 12px; border-bottom: 3px double ${NAVY}; }
        .letterhead img { width: 72px; height: 72px; object-fit: contain; border-radius: 6px; }
        .letterhead .info h1 { margin: 0; font-size: 22px; letter-spacing: 0.02em; color: ${NAVY}; }
        .letterhead .info p { margin: 3px 0 0; color: #555; font-size: 11px; }
        .stmt-title { text-align: center; margin: 16px 0 8px; }
        .stmt-title .name { font-size: 14px; font-weight: 800; letter-spacing: 0.16em; color: ${NAVY}; border-top: 1.5px solid ${NAVY}; border-bottom: 1.5px solid ${NAVY}; display: inline-block; padding: 3px 20px; }
        .stmt-title .period { margin-top: 6px; font-size: 11px; color: #555; }
        .grid { display: flex; gap: 8px; margin-top: 10px; }
        .col { flex: 1; min-width: 0; }
        .side-hd { text-align: center; padding: 6px 8px; background: ${NAVY}; color: #fff; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; font-size: 11px; border-radius: 4px 4px 0 0; }
        .side-hd.pay { background: ${RED}; }
        table { width: 100%; border-collapse: collapse; margin: 0; }
        thead th { text-align: left; font-size: 9.5px; letter-spacing: 0.06em; text-transform: uppercase; color: #fff; background: ${NAVY}; padding: 5px 6px; }
        thead th.right { text-align: right; }
        thead th.pay { background: ${RED}; }
        tbody td { padding: 4px 6px; font-size: 10.5px; border-bottom: 1px solid #eef0f4; }
        tbody td.right { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        tbody tr:nth-child(even) td { background: #fafbfc; }
        .bal-row td { background: #eef2f7; color: ${NAVY}; font-weight: 800; text-transform: uppercase; font-size: 10.5px; }
        .contra td { font-style: italic; color: #64748b; }
        .contra .ref { background: ${GOLD}; color: #fff; padding: 1px 6px; border-radius: 8px; font-size: 9px; font-weight: 800; font-style: normal; }
        .total-row td { background: ${NAVY}; color: #fff; font-weight: 800; padding: 6px; text-transform: uppercase; font-size: 11px; border-top: 2px solid ${NAVY}; border-bottom: 2px solid ${NAVY}; }
        .total-row.pay td { background: ${RED}; }
        .balance-note { text-align: center; margin-top: 14px; padding: 8px; border-radius: 4px; font-weight: 700; font-size: 11px; }
        .balance-note.ok { background: #e7f5ef; color: ${GREEN}; border: 1px solid ${GREEN}; }
        .balance-note.bad { background: #fdecec; color: ${RED}; border: 1px solid ${RED}; }
        .footer { display: flex; justify-content: space-between; margin-top: 20px; font-size: 9px; color: #888; }
        @page { size: A4 landscape; margin: 10mm; }
      </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  // ─── EXPORT PDF ───────────────────────────────────────────────────────────
  const handlePdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 30;

    let y = 28;
    const logoSize = 46;
    let textLeft = margin;
    if (data.church?.logo && String(data.church.logo).startsWith('data:image')) {
      try {
        const fmtType = data.church.logo.substring(11, data.church.logo.indexOf(';'));
        doc.addImage(data.church.logo, fmtType.toUpperCase(), margin, y, logoSize, logoSize);
        textLeft = margin + logoSize + 10;
      } catch (e) { /* skip */ }
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(11, 37, 69);
    doc.text(data.church?.name || 'Church', textLeft, y + 16);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(90);
    const subInfo = [data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ');
    if (subInfo) doc.text(subInfo, textLeft, y + 28);
    y += Math.max(logoSize, 32) + 6;
    doc.setDrawColor(11, 37, 69); doc.setLineWidth(1.0);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineWidth(0.4); doc.line(margin, y + 3, pageW - margin, y + 3);
    y += 14;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(11, 37, 69);
    const title = 'CASH BOOK';
    const titleW = doc.getTextWidth(title);
    const tx = (pageW - titleW) / 2;
    doc.setLineWidth(0.6);
    doc.line(tx - 14, y - 4, tx + titleW + 14, y - 4);
    doc.text(title, pageW / 2, y + 8, { align: 'center' });
    doc.line(tx - 14, y + 13, tx + titleW + 14, y + 13);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(90);
    doc.text(
      `For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`,
      pageW / 2, y + 26, { align: 'center' }
    );
    y += 40;

    const halfW = (pageW - margin * 2 - 12) / 2;

    // Receipts side header banner
    doc.setFillColor(11, 37, 69);
    doc.rect(margin, y, halfW, 18, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(255);
    doc.text('RECEIPTS (Dr)', margin + halfW / 2, y + 12, { align: 'center' });

    // Payments side header banner
    doc.setFillColor(185, 28, 28);
    doc.rect(margin + halfW + 12, y, halfW, 18, 'F');
    doc.text('PAYMENTS (Cr)', margin + halfW + 12 + halfW / 2, y + 12, { align: 'center' });

    y += 20;

    const buildBody = (rows, isReceipts) => {
      const body = rows.map(r => [
        fmtDateShort(r.date),
        {
          content: r.particulars + (r.type === 'contra' ? ' (contra)' : ''),
          styles: (r.type === 'bfwd' || r.type === 'cfwd')
            ? { fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] }
            : r.type === 'contra' ? { fontStyle: 'italic', textColor: [100, 116, 139] } : {},
        },
        {
          content: r.ref,
          styles: r.type === 'contra' ? { halign: 'center', fillColor: [200, 146, 17], textColor: 255, fontStyle: 'bold', fontSize: 8 } :
                  (r.type === 'bfwd' || r.type === 'cfwd') ? { halign: 'center', fontStyle: 'bold' } :
                  { halign: 'center' },
        },
        { content: r.cash ? fmt(r.cash) : '—',
          styles: {
            halign: 'right',
            fontStyle: (r.type === 'bfwd' || r.type === 'cfwd') ? 'bold' : 'normal',
            fillColor: (r.type === 'bfwd' || r.type === 'cfwd') ? [238, 242, 247] : null,
            textColor: r.cash ? [11, 37, 69] : [148, 163, 184],
          },
        },
        { content: r.bank ? fmt(r.bank) : '—',
          styles: {
            halign: 'right',
            fontStyle: (r.type === 'bfwd' || r.type === 'cfwd') ? 'bold' : 'normal',
            fillColor: (r.type === 'bfwd' || r.type === 'cfwd') ? [238, 242, 247] : null,
            textColor: r.bank ? [11, 37, 69] : [148, 163, 184],
          },
        },
      ]);
      const totals = isReceipts ? data.totals.receipts : data.totals.payments;
      const bgColor = isReceipts ? [11, 37, 69] : [185, 28, 28];
      body.push([
        { content: 'TOTAL', colSpan: 3,
          styles: { fillColor: bgColor, textColor: 255, fontStyle: 'bold', halign: 'right' } },
        { content: fmt(totals.cash),
          styles: { halign: 'right', fillColor: bgColor, textColor: 255, fontStyle: 'bold' } },
        { content: fmt(totals.bank),
          styles: { halign: 'right', fillColor: bgColor, textColor: 255, fontStyle: 'bold' } },
      ]);
      return body;
    };

    autoTable(doc, {
      startY: y,
      head: [[
        'Date', 'Particulars', 'Ref',
        { content: 'Cash (GH₵)', styles: { halign: 'right' } },
        { content: 'Bank (GH₵)', styles: { halign: 'right' } },
      ]],
      body: buildBody(data.receipts, true),
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: 3, lineColor: [230, 232, 238], lineWidth: 0.3 },
      headStyles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold', halign: 'left' },
      alternateRowStyles: { fillColor: [250, 251, 252] },
      columnStyles: {
        0: { cellWidth: 44 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 40, halign: 'center' },
        3: { cellWidth: 60, halign: 'right' },
        4: { cellWidth: 60, halign: 'right' },
      },
      margin: { top: 40, left: margin, right: margin + halfW + 12, bottom: 40 },
      tableWidth: halfW,
    });

    autoTable(doc, {
      startY: y,
      head: [[
        'Date', 'Particulars', 'Ref',
        { content: 'Cash (GH₵)', styles: { halign: 'right' } },
        { content: 'Bank (GH₵)', styles: { halign: 'right' } },
      ]],
      body: buildBody(data.payments, false),
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: 3, lineColor: [230, 232, 238], lineWidth: 0.3 },
      headStyles: { fillColor: [185, 28, 28], textColor: 255, fontStyle: 'bold', halign: 'left' },
      alternateRowStyles: { fillColor: [250, 251, 252] },
      columnStyles: {
        0: { cellWidth: 44 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 40, halign: 'center' },
        3: { cellWidth: 60, halign: 'right' },
        4: { cellWidth: 60, halign: 'right' },
      },
      margin: { top: 40, left: margin + halfW + 12, right: margin, bottom: 40 },
      tableWidth: halfW,
      didDrawPage: () => {
        doc.setFontSize(8); doc.setTextColor(120);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, pageH - 18, { align: 'right' });
        doc.text(`Generated ${new Date().toLocaleString('en-GH')}`, margin, pageH - 18);
      },
    });

    // Balance note
    let by = doc.lastAutoTable?.finalY || y;
    by += 14;
    if (by > pageH - 60) { doc.addPage(); by = 60; }
    if (data.totals.balanced) {
      doc.setDrawColor(15, 118, 110); doc.setFillColor(231, 245, 239);
    } else {
      doc.setDrawColor(185, 28, 28); doc.setFillColor(253, 236, 236);
    }
    doc.setLineWidth(0.6);
    doc.roundedRect(margin, by, pageW - margin * 2, 22, 4, 4, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.setTextColor(...(data.totals.balanced ? [15, 118, 110] : [185, 28, 28]));
    doc.text(
      data.totals.balanced
        ? 'Cash Book is balanced — Receipts (Cash & Bank) equal Payments (Cash & Bank).'
        : `Cash Book is out of balance. Cash diff: ${fmt(data.totals.receipts.cash - data.totals.payments.cash)}, Bank diff: ${fmt(data.totals.receipts.bank - data.totals.payments.bank)}`,
      pageW / 2, by + 14, { align: 'center' }
    );

    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    doc.save(`Cash_Book_${stamp.replace(/[\/\s]/g, '_')}.pdf`);
  };

  // ─── EXPORT EXCEL ─────────────────────────────────────────────────────────
  const handleExcel = () => {
    if (!data) return;
    const aoa = [];
    aoa.push([data.church?.name || 'Church']);
    if (data.church?.address) aoa.push([data.church.address]);
    const contact = [data.church?.phone, data.church?.email].filter(Boolean).join(' · ');
    if (contact) aoa.push([contact]);
    aoa.push([]);
    aoa.push(['CASH BOOK']);
    aoa.push([`For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`]);
    aoa.push([]);
    aoa.push(['RECEIPTS (Dr)', '', '', '', '', '', 'PAYMENTS (Cr)', '', '', '', '']);
    aoa.push(['Date', 'Particulars', 'Ref', 'Cash (GH₵)', 'Bank (GH₵)', '',
              'Date', 'Particulars', 'Ref', 'Cash (GH₵)', 'Bank (GH₵)']);
    const maxLen = Math.max(data.receipts.length, data.payments.length);
    for (let i = 0; i < maxLen; i++) {
      const r = data.receipts[i] || {};
      const p = data.payments[i] || {};
      aoa.push([
        r.date || '', r.particulars || '', r.ref || '',
        r.cash ? Number(r.cash) : '', r.bank ? Number(r.bank) : '',
        '',
        p.date || '', p.particulars || '', p.ref || '',
        p.cash ? Number(p.cash) : '', p.bank ? Number(p.bank) : '',
      ]);
    }
    aoa.push([
      '', '', 'TOTAL',
      Number(data.totals.receipts.cash), Number(data.totals.receipts.bank),
      '',
      '', '', 'TOTAL',
      Number(data.totals.payments.cash), Number(data.totals.payments.bank),
    ]);
    aoa.push([]);
    aoa.push([data.totals.balanced
      ? 'Cash Book is balanced — Receipts (Cash & Bank) equal Payments (Cash & Bank).'
      : `Out of balance. Cash diff: ${fmt(data.totals.receipts.cash - data.totals.payments.cash)}, Bank diff: ${fmt(data.totals.receipts.bank - data.totals.payments.bank)}`]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 12 }, { wch: 34 }, { wch: 10 }, { wch: 14 }, { wch: 14 },
      { wch: 2 },
      { wch: 12 }, { wch: 34 }, { wch: 10 }, { wch: 14 }, { wch: 14 },
    ];
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (const C of [3, 4, 9, 10]) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[addr];
        if (cell && typeof cell.v === 'number') cell.z = '#,##0.00';
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cash Book');
    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    XLSX.writeFile(wb, `Cash_Book_${stamp.replace(/[\/\s]/g, '_')}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Building cash book for {fmtDateLong(start)} — {fmtDateLong(end)}…
        </Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error" onClose={() => setError('')}>{error}</Alert>;
  if (!data) return null;

  const balanced = data.totals.balanced;

  return (
    <Box>
      {/* Action bar */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mb={2} flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrint}>Print</Button>
        <Button size="small" variant="outlined" startIcon={<GridOn />} onClick={handleExcel}
          sx={{ color: '#0F6C3D', borderColor: '#0F6C3D', '&:hover': { borderColor: '#0F6C3D', bgcolor: '#0F6C3D0d' } }}>
          Export Excel
        </Button>
        <Button size="small" variant="contained" startIcon={<PictureAsPdf />} onClick={handlePdf}
          sx={{ bgcolor: RED, '&:hover': { bgcolor: '#991B1B' } }}>
          Export PDF
        </Button>
      </Stack>

      {/* Statement paper */}
      <Paper
        elevation={2}
        sx={{
          border: '1px solid #E2E8F0', borderRadius: 2,
          p: { xs: 2, md: 3 }, bgcolor: '#fff',
          maxWidth: 1400, mx: 'auto',
          boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
        }}
      >
        <Box ref={printRef}>
          {/* Letterhead */}
          <Box className="letterhead" sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            pb: 1.5, borderBottom: `3px double ${RULE}`,
          }}>
            {data.church?.logo ? (
              <Box component="img" src={data.church.logo} alt="Logo"
                sx={{ width: 78, height: 78, objectFit: 'contain', borderRadius: 1 }} />
            ) : (
              <Box sx={{
                width: 78, height: 78, borderRadius: 1,
                bgcolor: `${NAVY}12`, color: NAVY,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 26,
              }}>
                {(data.church?.name || 'C').charAt(0).toUpperCase()}
              </Box>
            )}
            <Box className="info" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800}
                sx={{ color: NAVY, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                {data.church?.name || 'Church'}
              </Typography>
              {(data.church?.address || data.church?.phone || data.church?.email) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {[data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Title */}
          <Box className="stmt-title" sx={{ textAlign: 'center', mt: 2, mb: 1 }}>
            <Typography className="name" sx={{
              fontSize: 15, fontWeight: 800, letterSpacing: '0.16em', color: NAVY,
              borderTop: `1.5px solid ${NAVY}`, borderBottom: `1.5px solid ${NAVY}`,
              display: 'inline-block', px: 3, py: 0.5,
            }}>
              CASH BOOK
            </Typography>
            <Typography className="period" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              For the period <b>{fmtDateLong(data.period.start_date)}</b> to <b>{fmtDateLong(data.period.end_date)}</b>
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" mt={1} flexWrap="wrap">
              <Chip size="small" label={`Cash b/fwd GH₵ ${fmt(data.bfwd.cash)}`}
                sx={{ bgcolor: `${NAVY}15`, color: NAVY, fontWeight: 700 }} />
              <Chip size="small" label={`Bank b/fwd GH₵ ${fmt(data.bfwd.bank)}`}
                sx={{ bgcolor: `${NAVY}15`, color: NAVY, fontWeight: 700 }} />
              <Chip size="small" label={`Cash c/fwd GH₵ ${fmt(data.cfwd.cash)}`}
                sx={{ bgcolor: `${GOLD}20`, color: '#6B4A08', fontWeight: 700 }} />
              <Chip size="small" label={`Bank c/fwd GH₵ ${fmt(data.cfwd.bank)}`}
                sx={{ bgcolor: `${GOLD}20`, color: '#6B4A08', fontWeight: 700 }} />
            </Stack>
          </Box>

          {/* Two sides */}
          <Box className="grid" sx={{
            display: 'flex', gap: 1,
            flexDirection: { xs: 'column', md: 'row' },
            mt: 1.5,
          }}>
            {/* Receipts side */}
            <Box className="col" sx={{ flex: 1, minWidth: 0 }}>
              <Box className="side-hd" sx={{
                textAlign: 'center', bgcolor: NAVY, color: '#fff',
                py: 0.9, letterSpacing: '0.1em', fontWeight: 800,
                textTransform: 'uppercase', fontSize: 12,
                borderRadius: '4px 4px 0 0',
              }}>
                RECEIPTS (Dr)
              </Box>
              <CashBookSide rows={data.receipts} totals={data.totals.receipts} side="receipts" />
            </Box>

            {/* Payments side */}
            <Box className="col" sx={{ flex: 1, minWidth: 0 }}>
              <Box className="side-hd pay" sx={{
                textAlign: 'center', bgcolor: RED, color: '#fff',
                py: 0.9, letterSpacing: '0.1em', fontWeight: 800,
                textTransform: 'uppercase', fontSize: 12,
                borderRadius: '4px 4px 0 0',
              }}>
                PAYMENTS (Cr)
              </Box>
              <CashBookSide rows={data.payments} totals={data.totals.payments} side="payments" />
            </Box>
          </Box>

          {/* Balance check */}
          <Box className={`balance-note ${balanced ? 'ok' : 'bad'}`} sx={{
            mt: 2, p: 1.4, borderRadius: 1.2, textAlign: 'center',
            border: `1px solid ${balanced ? GREEN : RED}`,
            bgcolor: balanced ? '#e7f5ef' : '#fdecec',
            color:   balanced ? GREEN : RED,
            fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          }}>
            {balanced ? <CheckCircle sx={{ fontSize: 20 }} /> : <ErrorOutline sx={{ fontSize: 20 }} />}
            <Typography component="span" fontWeight={700}>
              {balanced
                ? 'Cash Book is balanced — Receipts (Cash & Bank) equal Payments (Cash & Bank).'
                : `Out of balance. Cash diff GH₵ ${fmt(data.totals.receipts.cash - data.totals.payments.cash)} · Bank diff GH₵ ${fmt(data.totals.receipts.bank - data.totals.payments.bank)}`}
            </Typography>
          </Box>

          {/* Footer */}
          <Box className="footer" sx={{
            display: 'flex', justifyContent: 'space-between', mt: 3,
            fontSize: 10, color: '#888',
          }}>
            <span>Generated {new Date().toLocaleString('en-GH')}</span>
            <span>{data.church?.name || ''}</span>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── One side (Receipts or Payments) ──────────────────────────────────────
function CashBookSide({ rows, totals, side }) {
  const isPay = side === 'payments';
  const barBg = isPay ? RED : NAVY;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ ...thStyle, background: barBg, width: 60 }}>Date</th>
          <th style={{ ...thStyle, background: barBg }}>Particulars</th>
          <th style={{ ...thStyle, background: barBg, width: 46, textAlign: 'center' }}>Ref</th>
          <th style={{ ...thStyle, background: barBg, textAlign: 'right', width: 90 }}>Cash (GH₵)</th>
          <th style={{ ...thStyle, background: barBg, textAlign: 'right', width: 90 }}>Bank (GH₵)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const isBal = r.type === 'bfwd' || r.type === 'cfwd';
          const isContra = r.type === 'contra';
          return (
            <tr key={`${side}-${i}`} style={{
              background: isBal ? '#eef2f7' : (i % 2 === 1 ? '#fafbfc' : 'transparent'),
            }}>
              <td style={{ ...cellStyle, whiteSpace: 'nowrap',
                fontWeight: isBal ? 800 : 400,
                color: isBal ? NAVY : '#334155' }}>
                {isBal ? '' : fmtDateShort(r.date)}
              </td>
              <td style={{ ...cellStyle,
                fontWeight: isBal ? 800 : (isContra ? 500 : 500),
                fontStyle: isContra ? 'italic' : 'normal',
                color: isBal ? NAVY : (isContra ? '#64748b' : '#1e293b'),
                textTransform: isBal ? 'uppercase' : 'none',
                letterSpacing: isBal ? '0.05em' : 'normal',
              }}>
                {r.particulars}
              </td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>
                {isContra ? (
                  <span style={{
                    display: 'inline-block', padding: '1px 8px',
                    background: GOLD, color: '#fff', borderRadius: 8,
                    fontSize: 10, fontWeight: 800,
                  }}>C</span>
                ) : isBal ? (
                  <span style={{ fontWeight: 800, color: NAVY, fontSize: 11 }}>{r.ref}</span>
                ) : (
                  <span style={{ fontSize: 11, color: '#64748b' }}>{r.ref}</span>
                )}
              </td>
              <td style={{ ...amountStyle,
                fontWeight: isBal ? 800 : (r.cash ? 700 : 400),
                color: r.cash ? NAVY : '#cbd5e1' }}>
                {r.cash ? fmt(r.cash) : '—'}
              </td>
              <td style={{ ...amountStyle,
                fontWeight: isBal ? 800 : (r.bank ? 700 : 400),
                color: r.bank ? NAVY : '#cbd5e1' }}>
                {r.bank ? fmt(r.bank) : '—'}
              </td>
            </tr>
          );
        })}
        <tr>
          <td colSpan={3} style={{
            background: barBg, color: '#fff', fontWeight: 800,
            padding: '10px 8px', fontSize: 12, textAlign: 'right',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            borderTop: `2px solid ${barBg}`,
          }}>
            TOTAL
          </td>
          <td style={{
            background: barBg, color: '#fff', fontWeight: 800,
            padding: '10px 8px', fontSize: 12, textAlign: 'right',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            borderTop: `2px solid ${barBg}`,
          }}>
            {fmt(totals.cash)}
          </td>
          <td style={{
            background: barBg, color: '#fff', fontWeight: 800,
            padding: '10px 8px', fontSize: 12, textAlign: 'right',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            borderTop: `2px solid ${barBg}`,
          }}>
            {fmt(totals.bank)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

// ─── Inline styles ─────────────────────────────────────────────────────────
const thStyle = {
  textAlign: 'left', fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: '#fff', background: NAVY, padding: '8px 8px', fontWeight: 700,
};
const cellStyle    = { padding: '5px 8px', fontSize: 11.5, borderBottom: '1px solid #eef0f4',
                       verticalAlign: 'top' };
const amountStyle  = { padding: '5px 8px', fontSize: 12, borderBottom: '1px solid #eef0f4',
                       textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                       fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                       verticalAlign: 'top' };
